import { useEffect, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, Button, Center, Spinner, Text, Box,
} from "@chakra-ui/react";
import { FaDownload } from "react-icons/fa";
import { PDFViewer } from "@react-pdf/renderer";
import CvDocument from "./CvDocument";
import { fetchCvData, downloadCvPdf } from "./useDownloadCv";

// Shows a live preview of the generated CV PDF, with a download button.
export default function CvPreviewModal({ isOpen, onClose, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true);
    setError(null);
    setData(null);
    fetchCvData()
      .then((d) => active && setData(d))
      .catch((e) => active && setError(e.message || "error"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isOpen]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadCvPdf(data, language);
    } finally {
      setDownloading(false);
    }
  };

  const t = (es, en) => (language === "es" ? es : en);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent bg="bg.card" color="white" maxH="90vh">
        <ModalHeader>{t("Vista previa del CV", "CV preview")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading && (
            <Center py={20}>
              <Spinner size="xl" color="brand.400" />
            </Center>
          )}
          {error && (
            <Box py={10} textAlign="center">
              <Text color="red.300" fontWeight="bold">
                {error === "no-profile"
                  ? t("Aún no hay datos del perfil. Agrega tu información en el panel.",
                       "No profile data yet. Add your info in the admin panel.")
                  : t("No se pudo generar la vista previa.", "Could not generate the preview.")}
              </Text>
            </Box>
          )}
          {data && !loading && !error && (
            <Box h="70vh" w="100%" borderRadius="md" overflow="hidden">
              <PDFViewer width="100%" height="100%" showToolbar={false}>
                <CvDocument data={data} language={language} />
              </PDFViewer>
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            {t("Cerrar", "Close")}
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<FaDownload />}
            onClick={handleDownload}
            isLoading={downloading}
            isDisabled={!data}
          >
            {t("Descargar CV", "Download CV")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
